import { useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  changePriceWatch,
  describePAApiError,
  getSpecialistFeed,
  startPriceRadar,
  type PriceWatch,
} from '../../../lib/api/team.js';
import { useSkillBack } from '../../../lib/skillBack.js';
import { PageShell } from '../../stage/PageShell.js';
import { Skeleton } from '../../ui/Skeleton.js';
import './radar.css';

const KICKER = 'Radar de preços';
const money = (cents: number) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(cents / 100);
const cents = (value: string) => Math.round(Number(value.replace(',', '.')) * 100);

function MoneyInput({
  value,
  onChange,
  placeholder,
  label,
  autoFocus,
}: {
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  label?: string;
  autoFocus?: boolean;
}) {
  return (
    <span className="rdp-money">
      <i>R$</i>
      <input
        autoFocus={autoFocus}
        aria-label={label}
        inputMode="decimal"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
      />
    </span>
  );
}

function Field({
  label,
  optional,
  children,
}: {
  label: string;
  optional?: boolean;
  children: ReactNode;
}) {
  return (
    <label className="rdp-field">
      <span>
        {label} {optional && <i>opcional</i>}
      </span>
      {children}
    </label>
  );
}

function WatchForm({ close }: { close?: () => void }) {
  const qc = useQueryClient();
  const [title, setTitle] = useState('');
  const [current, setCurrent] = useState('');
  const [target, setTarget] = useState('');
  const [url, setUrl] = useState('');
  const [error, setError] = useState('');
  const mutation = useMutation({
    mutationFn: () =>
      changePriceWatch({
        type: 'add',
        title,
        currentPriceCents: cents(current),
        targetPriceCents: cents(target),
        url: url || undefined,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['pa-specialist-feed', 'radar_precos'] });
      close?.();
    },
    onError: (err) => setError(describePAApiError(err, 'Não consegui guardar esse produto agora.')),
  });
  return (
    <section className="pgs-section rdp-form">
      <h2 className="pgs-h2">Qual preço vale a espera?</h2>
      <Field label="Produto">
        <input
          className="rdp-input"
          maxLength={120}
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Ex.: Air fryer 5 litros"
        />
      </Field>
      <div className="rdp-pair">
        <Field label="Preço encontrado">
          <MoneyInput value={current} onChange={setCurrent} placeholder="349,90" />
        </Field>
        <Field label="Meu preço-alvo">
          <MoneyInput value={target} onChange={setTarget} placeholder="299,00" />
        </Field>
      </div>
      <Field label="Link do produto" optional>
        <input
          className="rdp-input"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          placeholder="https://…"
        />
      </Field>
      <p className="rdp-hint">
        Eu começo com o que você viu. Depois, cada novo preço desenha a evolução.
      </p>
      {error && (
        <p className="rdp-error" role="alert">
          {error}
        </p>
      )}
      <div className="pgs-actions">
        {close && (
          <button className="pgs-btn" onClick={close}>
            Agora não
          </button>
        )}
        <button
          className="pgs-btn solid"
          disabled={!title.trim() || !current || !target || mutation.isPending}
          onClick={() => mutation.mutate()}
        >
          {mutation.isPending ? 'Guardando…' : 'Colocar no radar'}
        </button>
      </div>
    </section>
  );
}

/** Linha do histórico de preços, com o alvo tracejado. */
function Sparkline({ watch }: { watch: PriceWatch }) {
  const prices = watch.history.slice(-10).map((item) => item.priceCents);
  const min = Math.min(...prices, watch.targetPriceCents);
  const max = Math.max(...prices, watch.targetPriceCents);
  const range = Math.max(1, max - min);
  const points = prices
    .map(
      (price, index) =>
        `${prices.length === 1 ? 50 : (index / (prices.length - 1)) * 100},${42 - ((price - min) / range) * 34}`,
    )
    .join(' ');
  const targetY = 42 - ((watch.targetPriceCents - min) / range) * 34;
  return (
    <svg
      className="rdp-chart"
      viewBox="0 0 100 46"
      preserveAspectRatio="none"
      aria-label="Histórico de preço"
    >
      <path d={`M0 ${targetY}H100`} className="target" />
      <polyline points={points} className="line" />
      <circle
        cx={prices.length === 1 ? 50 : 100}
        cy={42 - ((prices.at(-1)! - min) / range) * 34}
        r="2.3"
      />
    </svg>
  );
}

function ProductRow({ watch, automatic }: { watch: PriceWatch; automatic: boolean }) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [price, setPrice] = useState('');
  const [error, setError] = useState('');
  const mutation = useMutation({
    mutationFn: changePriceWatch,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['pa-specialist-feed', 'radar_precos'] });
      setEditing(false);
      setPrice('');
    },
    onError: (err) => setError(describePAApiError(err, 'Não consegui atualizar esse preço agora.')),
  });
  const { targetReached, changePercent, bestPriceCents } = watch.metrics;
  const gap = watch.currentPriceCents - watch.targetPriceCents;
  // Quanto do caminho até o alvo já foi percorrido (alvo / preço atual).
  const closeness = targetReached
    ? 100
    : Math.max(4, Math.min(100, (watch.targetPriceCents / watch.currentPriceCents) * 100));
  return (
    <article className={`pgs-row rdp-card${targetReached ? ' ok' : ''}`}>
      <div className="rdp-top">
        <span className="pgs-rowt">
          <b>{watch.title}</b>
          <small>
            {targetReached
              ? 'Entrou no seu alvo'
              : changePercent < 0
                ? 'O preço caiu'
                : 'Em observação'}
          </small>
        </span>
        <button
          className="rdp-remove"
          aria-label={`Tirar ${watch.title} do radar`}
          onClick={() => mutation.mutate({ type: 'delete', id: watch.id })}
        >
          ×
        </button>
      </div>
      <div className="rdp-prices">
        <span>
          <small>Agora</small>
          <b>{money(watch.currentPriceCents)}</b>
          <em className={changePercent < 0 ? 'down' : ''}>
            {changePercent > 0 ? '+' : ''}
            {changePercent}% desde o início
          </em>
        </span>
        <span>
          <small>Seu alvo</small>
          <b>{money(watch.targetPriceCents)}</b>
          <em>melhor: {money(bestPriceCents)}</em>
        </span>
      </div>
      <div className="pgs-bar">
        <span>{targetReached ? 'Dentro do alvo' : 'Falta para o alvo'}</span>
        <b>{targetReached ? (gap < 0 ? `${money(-gap)} abaixo` : 'no alvo') : money(gap)}</b>
        <i>
          <em style={{ width: `${closeness}%` }} />
        </i>
      </div>
      {watch.history.length > 1 && <Sparkline watch={watch} />}
      {editing ? (
        <div className="rdp-observe">
          <MoneyInput
            autoFocus
            label="Novo preço"
            value={price}
            onChange={setPrice}
            placeholder="Novo preço"
          />
          <button
            className="pgs-btn solid"
            disabled={!price}
            onClick={() =>
              mutation.mutate({ type: 'observe', id: watch.id, priceCents: cents(price) })
            }
          >
            Guardar
          </button>
          <button className="pgs-btn" onClick={() => setEditing(false)}>
            Cancelar
          </button>
        </div>
      ) : (
        <div className="pgs-actions">
          <button className="pgs-btn" onClick={() => setEditing(true)}>
            Registrar preço visto
          </button>
          {automatic && watch.provider === 'mercado_livre' && (
            <button
              className="pgs-btn"
              onClick={() => mutation.mutate({ type: 'refresh', id: watch.id })}
            >
              Conferir anúncio
            </button>
          )}
          {watch.url && (
            <a className="pgs-btn rdp-link" href={watch.url} target="_blank" rel="noreferrer">
              Abrir produto
            </a>
          )}
        </div>
      )}
      {error && (
        <p className="rdp-error" role="alert">
          {error}
        </p>
      )}
    </article>
  );
}

function AskNote() {
  return (
    <div className="pgs-note">
      <i>“</i>
      <span>
        <b>Pode me perguntar “algum preço caiu?”</b>
        <small>Eu comparo somente valores registrados.</small>
      </span>
    </div>
  );
}

function Dashboard({
  watches,
  automatic,
  onBack,
}: {
  watches: PriceWatch[];
  automatic: boolean;
  onBack: () => void;
}) {
  const [adding, setAdding] = useState(false);
  const reached = watches.filter((watch) => watch.metrics.targetReached);
  const drops = watches.filter((watch) => watch.metrics.changePercent < 0);
  const droppedOnly = drops.filter((watch) => !watch.metrics.targetReached);
  const title = reached.length
    ? `${reached.length} ${reached.length === 1 ? 'preço chegou' : 'preços chegaram'} ao alvo`
    : `${watches.length} ${watches.length === 1 ? 'produto' : 'produtos'} no radar`;
  const lead = drops.length
    ? `${drops.length} com queda desde o primeiro registro.`
    : 'Estou de olho. Você decide quando vale a compra.';
  return (
    <PageShell kicker={KICKER} title={title} lead={lead} onBack={onBack}>
      <div className="pgs-figure">
        <b>
          {reached.length}/{watches.length}
        </b>
        <small>
          {reached.length
            ? 'dentro da faixa que você escolheu'
            : 'no alvo por enquanto; cada registro melhora a comparação'}
        </small>
      </div>
      {reached.map((watch) => (
        <div className="pgs-note rdp-good" key={`alvo-${watch.id}`}>
          <i>✓</i>
          <span>
            <b>{watch.title} entrou no seu alvo</b>
            <small>
              Agora {money(watch.currentPriceCents)}, alvo {money(watch.targetPriceCents)}. Confira
              frete e vendedor antes de comprar.
            </small>
          </span>
        </div>
      ))}
      {droppedOnly.length > 0 && (
        <div className="pgs-note">
          <i>↓</i>
          <span>
            <b>
              {droppedOnly.length === 1
                ? `${droppedOnly[0]!.title} caiu ${Math.abs(droppedOnly[0]!.metrics.changePercent)}%`
                : `${droppedOnly.length} produtos com preço em queda`}
            </b>
            <small>Ainda acima do alvo. Sigo acompanhando.</small>
          </span>
        </div>
      )}
      <section className="pgs-section">
        <h2 className="pgs-h2">
          No radar <small>{watches.length}</small>
        </h2>
        {watches.map((watch) => (
          <ProductRow key={watch.id} watch={watch} automatic={automatic} />
        ))}
      </section>
      {adding ? (
        <WatchForm close={() => setAdding(false)} />
      ) : (
        <div className="pgs-actions">
          <button className="pgs-btn solid" onClick={() => setAdding(true)}>
            Acompanhar outro produto
          </button>
        </div>
      )}
      <AskNote />
      <p className="rdp-trust">
        Preço muda rápido. Confira valor, frete e vendedor antes de comprar.
      </p>
    </PageShell>
  );
}

export function RadarPrecosView() {
  const qc = useQueryClient();
  const back = useSkillBack();
  const query = useQuery({
    queryKey: ['pa-specialist-feed', 'radar_precos'],
    queryFn: () => getSpecialistFeed('radar_precos'),
  });
  const start = useMutation({
    mutationFn: startPriceRadar,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['pa-specialist-feed', 'radar_precos'] }),
  });
  const config = query.data?.config as
    | {
        configured?: boolean | undefined;
        watches?: PriceWatch[] | undefined;
        automaticMercadoLivreAvailable?: boolean;
      }
    | undefined;

  if (query.isError)
    return (
      <PageShell kicker={KICKER} title="Não consegui abrir seu histórico agora" onBack={back}>
        <div className="pgs-actions">
          <button className="pgs-btn solid" onClick={() => query.refetch()}>
            Tentar novamente
          </button>
        </div>
      </PageShell>
    );

  if (config && !config.configured)
    return (
      <PageShell
        kicker={KICKER}
        title="O preço muda. Seu alvo fica."
        lead="Guardo o valor que você encontrou, acompanho a queda e mostro a evolução com clareza."
        onBack={back}
      >
        <section className="pgs-section">
          <h2 className="pgs-h2">Um exemplo</h2>
          <div className="pgs-row">
            <span className="pgs-rowt">
              <b>Seu alvo</b>
              <small>O valor que faria sentido para você.</small>
            </span>
            <span className="pgs-val">R$ 299</span>
          </div>
          <div className="pgs-row">
            <span className="pgs-rowt">
              <b>Hoje</b>
              <small>O preço que você viu na loja.</small>
            </span>
            <span className="pgs-val">R$ 347</span>
          </div>
        </section>
        <div className="pgs-actions">
          <button
            className="pgs-btn solid"
            disabled={start.isPending}
            onClick={() => start.mutate()}
          >
            {start.isPending ? 'Ligando o radar…' : 'Acompanhar um produto'}
          </button>
        </div>
        <div className="pgs-note">
          <i>!</i>
          <span>
            <b>Sem promoção inventada</b>
            <small>Você confirma cada preço que entra no radar.</small>
          </span>
        </div>
      </PageShell>
    );

  if (config?.configured && !config.watches?.length)
    return (
      <PageShell
        kicker={KICKER}
        title="Qual produto entra no radar?"
        lead="Comece com o preço de hoje e o valor que faria sentido para você."
        onBack={back}
      >
        <WatchForm />
      </PageShell>
    );

  if (config?.configured)
    return (
      <Dashboard
        watches={config.watches ?? []}
        automatic={Boolean(config.automaticMercadoLivreAvailable)}
        onBack={back}
      />
    );

  return (
    <PageShell kicker={KICKER} title="Abrindo o radar" onBack={back}>
      <Skeleton variant="rows" label="Abrindo o radar de preços" />
    </PageShell>
  );
}
